import React from 'react';
import useMediaQuery from "@mui/system/useMediaQuery";
import {Grid, Typography} from "@mui/joy";
import {useTheme} from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import {
    BasicModalContent,
    DropzoneArea,
    uploadFilesRequest
} from "@dreebit-registry/vsm-microservice-components";
import _ from "lodash";
import {InstalledBaseNode} from "../../codegen/graphql";
import NodeModalTitle from "./NodeModalTitle";
import {useTranslation} from "react-i18next";
import {UploadRounded} from "@mui-symbols-material/w200";
import {
    DescriptionFilledRounded,
    UploadRounded as UploadRoundedW300
} from "@mui-symbols-material/w300";
import {useSnackbar} from "../../provider/snackbar";
import {Box, Button, Checkbox, FormControlLabel} from "@mui/material";
import {useMutation, useQuery} from "@apollo/client";
import {CreateImportInstalledBaseTask} from "../../graphql/mutations/createImportInstalledBaseTask";
import {BackgroundTaskImportTdas} from "../../graphql/queries/backgroundTaskImportTdas";
import {useKeycloak} from "@react-keycloak/web";
import {toast} from "react-toastify";
import {isSystemAdminToken} from "../../utils/systemAdmin";

interface Props {
    node: InstalledBaseNode;
    refetchFunction?: () => void;
    // onStartUpload: (files: File[]) => void
    open: boolean,
    onClose?: () => void,
}

const CsvStructureImportModal = ({node, refetchFunction, onClose, open}: Props) => {
    const theme = useTheme();
    const { keycloak } = useKeycloak();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const {t} = useTranslation();
    const [files, setFiles] = React.useState<File[]>([]);

    const { startProgress    } = useSnackbar();

    const [createImportInstalledBaseTaskMutation] = useMutation(CreateImportInstalledBaseTask)

    // Only system admins may move existing assets; everyone else always sends false.
    // For admins without the TDA (older gateway, query error) the option is neither shown nor sent.
    const isSystemAdmin = React.useMemo(() => isSystemAdminToken(keycloak.token), [keycloak.token]);
    const {data: tdaData} = useQuery(BackgroundTaskImportTdas, {skip: !isSystemAdmin});
    const moveExistingAssetsTda = _.find(
        _.get(tdaData, 'backgroundTaskList.typeDefinitionAttributeList.typeDefinitionAttributes', []) as any[],
        {index: 'moveExistingAssets'}
    );
    const [moveExistingAssets, setMoveExistingAssets] = React.useState<boolean | undefined>(undefined);
    const moveExistingAssetsValue = moveExistingAssets ?? (moveExistingAssetsTda?.value === true || moveExistingAssetsTda?.value === 'true');

    const handleUpload = async (loadedFiles: File[]) => {
        if (loadedFiles && loadedFiles.length === 1) {
            if (onClose) onClose()

            const uploadedFiles = await uploadFilesRequest(files, keycloak.token);
            const uploadedFileId = uploadedFiles[0]?.fileId || undefined;

            if (!uploadedFileId) {
                toast.error(t('Error uploading the file'))
            } else {
                const importResponse = await createImportInstalledBaseTaskMutation({
                    variables: {
                        input: {
                            importFileUuid: uploadedFileId,
                            parentInstalledBaseNodeId: node.id,
                            ...(!isSystemAdmin
                                ? {moveExistingAssets: false}
                                : moveExistingAssetsTda ? {moveExistingAssets: moveExistingAssetsValue} : {})
                        }
                    }
                })
                const taskId = _.get(importResponse, 'data.createImportInstalledBaseTask.id');

                startProgress("Processing...", taskId);
            }
        }
    };

    return <Dialog open={open} onClose={onClose} fullScreen={isFullScreen}>
        <BasicModalContent
            title={<NodeModalTitle prefix={t('Import nodes under ')} node={node}/>}
            onClose={onClose}
        >

            <Box
                borderRadius="4px"
                border="1px solid #ccc"
                padding="12px 16px"
                marginTop="8px"
            >
                <Typography level="body-md">
                    Use the import function to import hierarchical structures from a CSV
                    file into the Installed Base. When doing so, please consider the CSV
                    formatting based on this{" "}
                    <a
                        href="/example-csv-import.csv"
                        download
                        style={{
                            display: "inline-flex",
                            alignItems: "center",       // sorgt dafür, dass Icon und Text mittig stehen
                            verticalAlign: "middle",    // damit der gesamte Link im Textfluss mittig bleibt
                            gap: "2px",
                            textDecoration: "none",
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                            paddingBottom: "4px"
                        }}
                    >
                        <DescriptionFilledRounded fontSize="small" style={{verticalAlign: "middle"}}/>
                        example.csv
                    </a>
                    .
                </Typography>
            </Box>

            <Box pt={2}>
                <DropzoneArea
                    theme={theme}
                    fileObjects={files}
                    onChange={(loadedFiles: File[]) => setFiles(loadedFiles)}
                    onDelete={() => {
                    }}
                    filesLimit={1}
                    maxFileSize={999999999}
                    showPreviewsInDropzone={true}

                    showFileNames={true}
                    dropzoneText={t("Click here to upload a CSV file or drag the file to this area to upload.")}
                    dropzoneClass={"custom-file-upload-dropzone"}
                    dropzoneParagraphClass={"custom-file-upload-textbox"}
                    showAlerts={['error']}
                    Icon={UploadRounded}
                    classes={{
                        text: "dropzone-text",
                        root: "dropzone-root",
                        icon: "dropzone-icon",
                    }}
                    previewGridClasses={{
                        item: "preview-item"
                    }}
                />
            </Box>
            {!isSystemAdmin && (
                <Box pt={2}>
                    <Typography level="body-sm" data-testid="csv-import-existing-assets-hint">
                        {t('Assets that already exist are not moved by the import.')}
                    </Typography>
                </Box>
            )}
            {isSystemAdmin && moveExistingAssetsTda && (
                <Box pt={2}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={moveExistingAssetsValue}
                                onChange={(event) => setMoveExistingAssets(event.target.checked)}
                                inputProps={{"data-testid": "csv-import-move-existing-assets"} as React.InputHTMLAttributes<HTMLInputElement>}
                            />
                        }
                        label={<Typography level="body-md">{moveExistingAssetsTda.name}</Typography>}
                    />
                    {moveExistingAssetsTda.hint && (
                        <Typography level="body-sm" sx={{pl: 4}}>
                            {moveExistingAssetsTda.hint}
                        </Typography>
                    )}
                </Box>
            )}
            <Box pt={2}>
                <Grid container spacing={1}>
                    <Grid xs={6}>
                        <Button
                            fullWidth
                            variant='outlined'
                            onClick={onClose}
                        >
                            {t("Cancel")}
                        </Button>
                    </Grid>
                    <Grid xs={6}>
                        <Button
                            variant="contained"
                            disabled={files.length !== 1}
                            fullWidth
                            onClick={() => handleUpload(files)}
                            startIcon={<UploadRoundedW300 />}
                        >
                            {t("Upload")}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </BasicModalContent>
    </Dialog>
};

export default CsvStructureImportModal;
